import { Value } from '@gameonly/core';
import { InputAdornment, TextField, TextFieldProps } from '@mui/material';
import {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  WheelEvent,
} from 'react';

type NumberInputProps = Omit<TextFieldProps, 'type' | 'onChange'> & {
  onChange: (
    event: ChangeEvent<HTMLInputElement>,
    value: number | undefined,
  ) => void;
  unit: string | undefined;
  disabled: boolean;
  currentValue: Value;
  initialValue: Value;
};

export const isEmptyValue = (value: Value): boolean => {
  if (value === undefined) {
    return true;
  }
  if (typeof value === 'number') {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim().length === 0;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime());
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false; // Default fallback, should not reach here with the defined types
};

const NumberInput = ({
  onChange,
  unit,
  disabled,
  currentValue,
  initialValue,
  ...props
}: NumberInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleOnChange = (event: FocusEvent<HTMLInputElement>) => {
    let value: number | undefined = parseFloat(event.target.value);
    if (isNaN(value)) {
      value = undefined;
    }
    if (typeof value !== 'undefined' && Number.isFinite(value)) {
      if (
        Number.isFinite(props.inputProps?.min) &&
        value < props.inputProps?.min
      ) {
        value = props.inputProps?.min;
      } else if (
        Number.isFinite(props.inputProps?.max) &&
        value > props.inputProps?.max
      ) {
        value = props.inputProps?.max;
      }
    }

    // avoid triggering update when user enters empty input then blur without any changes
    if (
      currentValue !== value &&
      value === undefined &&
      currentValue === '' &&
      isEmptyValue(initialValue)
    )
      return;

    if (isEmptyValue(event.target.value)) {
      value = typeof initialValue === 'number' ? initialValue : undefined;
      inputRef.current && (inputRef.current.value = value?.toString() || '');
    }

    onChange && onChange(event, value);
  };

  useEffect(() => {
    if (!inputRef.current) {
      return;
    }
    inputRef.current.value =
      typeof props.defaultValue !== 'undefined' && props.defaultValue !== null
        ? (props.defaultValue as any).toString() || ''
        : '';
  }, [props.defaultValue]);

  const handleWheel = (e: WheelEvent<HTMLDivElement>) => {
    (e.target as HTMLElement).blur();
  };

  const handleOnKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    if (!value) {
      return;
    }

    if (
      (Number.isFinite(props.inputProps?.min) &&
        value < props.inputProps?.min) ||
      (Number.isFinite(props.inputProps?.max) && value > props.inputProps?.max)
    ) {
      input.value = input.value.slice(0, -1);
    }
  };

  return (
    <TextField
      {...props}
      onBlur={handleOnChange}
      onWheel={handleWheel}
      type="number"
      inputRef={inputRef}
      disabled={disabled}
      onKeyUp={handleOnKeyUp}
      InputProps={{
        endAdornment: !!unit ? (
          <InputAdornment position="end">{unit}</InputAdornment>
        ) : null,
      }}
    />
  );
};
export default NumberInput;
